type MaintenancePhase = 'scheduled' | 'active' | 'completed';

type MaintenanceWindow = {
  start: Date;
  end: Date;
  phase: MaintenancePhase;
};

type MaintenanceConfig = {
  start?: string;
  end?: string;
  noticeHours?: string;
  noticeUntil?: string;
  now?: Date;
};

const DEFAULT_NOTICE_HOURS = 72;
const TIME_ZONE = 'Europe/Madrid';
const HOUR_MS = 60 * 60 * 1000;

function parseDate(value?: string) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function parseNoticeHours(value?: string) {
  const hours = value ? Number(value) : NaN;
  return Number.isFinite(hours) && hours >= 0 ? hours : DEFAULT_NOTICE_HOURS;
}

function getMaintenanceWindow({
  start: startValue = import.meta.env.VITE_MAINTENANCE_START,
  end: endValue = import.meta.env.VITE_MAINTENANCE_END,
  noticeHours = import.meta.env.VITE_MAINTENANCE_NOTICE_HOURS,
  noticeUntil = import.meta.env.VITE_MAINTENANCE_NOTICE_UNTIL,
  now = new Date(),
}: MaintenanceConfig = {}): MaintenanceWindow | null {
  const start = parseDate(startValue);
  const end = parseDate(endValue);

  if (!start || !end || end <= start) return null;

  const noticeFrom = start.getTime() - parseNoticeHours(noticeHours) * HOUR_MS;
  const until = parseDate(noticeUntil);
  const visibleUntil = until && until > end ? until : end;

  if (now.getTime() < noticeFrom || now >= visibleUntil) return null;

  const phase: MaintenancePhase =
    now < start ? 'scheduled' : now < end ? 'active' : 'completed';

  return {start, end, phase};
}

function getTimeZoneName(date: Date) {
  return (
    new Intl.DateTimeFormat('en-GB', {timeZone: TIME_ZONE, timeZoneName: 'short'})
      .formatToParts(date)
      .find(part => part.type === 'timeZoneName')?.value ?? ''
  );
}

function formatMaintenanceTimes({start, end}: MaintenanceWindow, language: string) {
  const locale = language.split('-')[0] === 'en' ? 'en-GB' : language;
  const formatter = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TIME_ZONE,
  });
  const dayFormatter = new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    timeZone: TIME_ZONE,
  });
  const timeZoneName = getTimeZoneName(end);

  return {
    range: `${formatter.formatRange(start, end)} ${timeZoneName}`,
    end: `${formatter.format(end)} ${timeZoneName}`,
    nextDay: dayFormatter.format(new Date(start.getTime() + 24 * HOUR_MS)),
  };
}

export {formatMaintenanceTimes, getMaintenanceWindow};
export type {MaintenancePhase, MaintenanceWindow};
